import React, { FC } from "react";
import { ChatDetail, ChatMemberJoinDetail, ChatMemberLeaveDetail } from "../../store/ChatAction";
import { ChatUserInfo } from "@xapp/stentor-chat-widget";
import { ROUTER_USER } from "../../xapp/StentorRouterChat";

export interface Props {
    readonly message: ChatDetail;
    readonly time?: string;
}

function isServerUser(user: ChatUserInfo): boolean {
    return user.nick?.endsWith(ROUTER_USER);
}

function shouldShowDivider(message: ChatDetail): boolean {
    return (message.type === "chat.memberjoin" && (message as ChatMemberJoinDetail).showDivider) ||
           (message.type === "chat.memberleave" && (message as ChatMemberLeaveDetail).showDivider);
}

function getMessageByType(msg: ChatDetail) {
    switch (msg.type) {
        case "chat.memberjoin":
            // Use custom message if provided, otherwise use default
            return (msg as ChatMemberJoinDetail).message || `${msg.user.displayName || "Bot"} has joined the chat`;
        case "chat.memberleave":
            // Use custom message if provided, otherwise use default
            return (msg as ChatMemberLeaveDetail).message || `${msg.user.displayName || "Bot"} has left the chat`;
        case "chat.systemmessage":
            return msg.message;
        case "chat.rating":
            if (!msg.newRating) {
                return "You have removed the chat rating";
            } else {
                const rating = convertToSentenceCase(msg.newRating);
                return `You have rated the chat service ${rating}`;
            }
        default:
            return JSON.stringify(msg);
    }
}

const SystemMessage: FC<Props> = (props) => {
    if (!isServerUser(props.message.user)) {
        const message = props.message;
        const showDivider = shouldShowDivider(message);
        const avatarUrl = message.user?.avatarPath;

        return (
            <div className="system-msg-container">
                {showDivider && avatarUrl && (
                    <div className="system-msg-divider">
                        <div className="divider-line"></div>
                        <div className="divider-avatar">
                            <img src={avatarUrl} alt={`${message.user.displayName || "User"}'s avatar`} />
                        </div>
                        <div className="divider-line"></div>
                    </div>
                )}
                <span className="message-sr-only">{"at " + props.time + " system message"}</span>
                {/*
                 * SECURITY NOTE: System messages are rendered as plain text without sanitization.
                 * This is safe because system messages originate from trusted server sources only.
                 * The isServerUser check above ensures messages come from authenticated backend servers.
                 * Custom messages (via showDivider/message fields) are also server-controlled.
                 */}
                <span className="system-msg">{getMessageByType(props.message)}</span>
            </div>
        );
    } else {
        return null;
    }
};

function convertToSentenceCase(s: string) {
    return s[0].toUpperCase() + s.slice(1);
}

export default SystemMessage;
